/**
 * CheckboxField - Checkbox field component
 *
 * Task: T-P4-D2-F4b-4
 * Feature: P4-D2-F4 Web App Forms UI
 *
 * Handles two modes based on field.options:
 * - Consent mode (no options): Single checkbox, value is boolean
 * - Multi-select mode (with options): Multiple checkboxes, value is string[]
 */

import type { ChangeEvent } from 'react';
import type { CheckboxFieldProps } from './types';
import { useFieldPart } from './fieldParts';
import { FieldWrapper } from './FieldWrapper';
import { CheckIcon } from './Icons';
import styles from './fields.module.css';

/**
 * CheckboxField renders either a single consent checkbox or a multi-select group.
 */
export function CheckboxField({
  field,
  value,
  onChange,
  onBlur,
  error,
  style,
  nonInteractive,
}: CheckboxFieldProps) {
  const options = field.options ?? [];
  const isMultiSelect = options.length > 0;

  // Multi-select mode: field.options is present
  if (isMultiSelect) {
    return (
      <MultiSelectCheckbox
        field={field}
        value={value as string[]}
        onChange={(v) => onChange(v)}
        onBlur={onBlur}
        error={error}
        style={style}
        nonInteractive={nonInteractive}
      />
    );
  }

  // Consent mode: single checkbox
  return (
    <ConsentCheckbox
      field={field}
      value={value as boolean}
      onChange={(v) => onChange(v)}
      onBlur={onBlur}
      error={error}
      style={style}
      nonInteractive={nonInteractive}
    />
  );
}

/**
 * Single consent-style checkbox (boolean value)
 */
function ConsentCheckbox({
  field,
  value,
  onChange,
  onBlur,
  error,
  style,
  nonInteractive,
}: {
  field: CheckboxFieldProps['field'];
  value: boolean;
  onChange: (value: boolean) => void;
  onBlur: () => void;
  error?: string;
  style?: CheckboxFieldProps['style'];
  nonInteractive?: boolean;
}) {
  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    onChange(e.target.checked);
  };

  return (
    <FieldWrapper
      fieldId={field.fieldId}
      label=""
      error={error}
      style={style}
    >
      <label className={styles.checkboxGroup}>
        <input
          type="checkbox"
          id={field.fieldId}
          name={field.fieldId}
          className={styles.checkboxInput}
          checked={value}
          onChange={handleChange}
          onBlur={onBlur}
          aria-disabled={nonInteractive || undefined}
          aria-invalid={!!error}
          aria-describedby={error ? `${field.fieldId}-error` : undefined}
          style={nonInteractive ? { pointerEvents: 'none' } : undefined}
        />
        <span
          className={`${styles.checkboxBox} ${error ? styles.hasError : ''}`}
        >
          <CheckIcon />
        </span>
        <span className={styles.checkboxLabel}>
          {field.label}
          {field.required && <span className={styles.required}> *</span>}
        </span>
      </label>
      {/* The statement IS the question here, so help text sits under the row (Gabriel 2026-09-02). */}
      {field.helpText && <span className={`${styles.helpText} ${styles.consentHelp}`}>{field.helpText}</span>}
    </FieldWrapper>
  );
}

/**
 * Multi-select checkbox group (string[] value)
 */
function MultiSelectCheckbox({
  field,
  value,
  onChange,
  onBlur,
  error,
  style,
  nonInteractive,
}: {
  field: CheckboxFieldProps['field'];
  value: string[];
  onChange: (value: string[]) => void;
  onBlur: () => void;
  error?: string;
  style?: CheckboxFieldProps['style'];
  nonInteractive?: boolean;
}) {
  const options = field.options ?? [];
  const part = useFieldPart();

  const handleChange = (optionValue: string, checked: boolean) => {
    if (checked) {
      onChange([...value, optionValue]);
    } else {
      onChange(value.filter((v) => v !== optionValue));
    }
  };

  return (
    <FieldWrapper
      fieldId={field.fieldId}
      label={field.label}
      required={field.required}
      helpText={field.helpText}
      error={error}
      style={style}
      isGroup
    >
      <div
        className={styles.checkboxMultiGroup}
        role="group"
        aria-labelledby={`${field.fieldId}-label`}
      >
        {options.map((option) => {
          const isChecked = value.includes(option.value);
          const optionId = `${field.fieldId}-${option.value}`;

          return (
            <label key={option.value} className={styles.checkboxOption} {...part('choice', isChecked)}>
              <input
                type="checkbox"
                id={optionId}
                name={field.fieldId}
                value={option.value}
                className={styles.checkboxInput}
                checked={isChecked}
                onChange={(e) => handleChange(option.value, e.target.checked)}
                onBlur={onBlur}
                aria-disabled={nonInteractive || undefined}
                aria-invalid={!!error}
                style={nonInteractive ? { pointerEvents: 'none' } : undefined}
              />
              <span
                className={`${styles.checkboxBox} ${
                  error ? styles.hasError : ''
                }`}
              >
                <CheckIcon />
              </span>
              <span className={styles.checkboxLabel}>{option.label}</span>
            </label>
          );
        })}
      </div>
    </FieldWrapper>
  );
}
